import {useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import {Page, Group, Layout, Button, Grid, Card, Icon} from "@/comn/components";
import { useForm, useFetch, useStore, useToast, useModal, useResource, useGrid, usePage, useTheme } from "@/comn/hooks";
import { Link } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import {SF_REVW_SRCH, SG_REVW_LIST, URLS, APIS} from "@/tra/tancis/ptli/revw/cstmr/services/PtliRevwService";

/**
 * PTLI0707011Q Component
 * -----------------------
 * - Dashboard page showing customer review statistics.
 * - Displays summary cards with total counts:
 *   - Total Users Submit Review
 *   - Good Experience Review
 *   - Bad Experience Review
 *   - Total Process Registered
 * - Includes charts:
 *   - Donut chart for BSOP Class Count (business operation classification)
 *   - Stacked bar chart for monthly good vs bad reviews
 * - Fetches data via APIs (`APIS.getDashCnt`, `APIS.getMonthStatistic`, `APIS.getBsopClsfCnt`)
 * - Uses `useFetch`, `useStore`, `useResource`, `useTheme` and other hooks for state and UI management.
 * - Uses reusable `CountCard` component for displaying summary cards.
 */

export const PTLI0707011Q = (props: any) => {
    // Props from parent
    const { condData, callback, cstmRoleList } = props;

    // Page utilities and state management
    const { pgeUid } = usePage({ 
        pgeUid: "ptli0707012q", 
        auth: ["auth_ptli0019"]
    });
    const modal = useModal();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Local state
    const [monthlyData, setMonthlyData] = useState<any[]>([]);
    const [bsopCounts, setBsopCounts] = useState<any[]>([]);
    const [countData, setCountData] = useState<CountItem[]>([]);
    const [countsData, setCountsData] = useState<any | null>(null);

    const chartTheme = theme.isDark === "true" ? "dark" : undefined;



    // Type for summary cards
    interface CountItem {
        title: string;
        count: number;
        icon: React.ReactNode;
    }

    // Load reference data (common codes)
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "COM0024" },
        ],
    });

    // Extract arrays for bar chart
    const months = monthlyData.map((item) => item.monthName);
    const goodReviews = monthlyData.map((item) => item.goodReviews);
    const badReviews = monthlyData.map((item) => item.badReviews);

    // Bar chart configuration
    const option = {
        tooltip: { trigger: "axis" },
        legend: { data: ["Good Reviews", "Bad Reviews"] },
        xAxis: { type: "category", data: months },
        yAxis: { type: "value" },
        series: [
            {
                name: "Good Reviews",
                type: "bar",
                stack: "reviews",
                data: goodReviews,
                itemStyle: { color: "#f1c40f" }, // yellow
            },
            {
                name: "Bad Reviews",
                type: "bar",
                stack: "reviews",
                data: badReviews,
                itemStyle: { color: "#e74c3c" }, // red
            },
        ],
    };

    // Donut chart data & configuration
    const donutData = bsopCounts.map(item => ({ name: item.bsopClsfCd, value: item.count }));

    const donutOption = {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { orient: 'vertical', left: 'left', data: donutData.map(d => d.name) },
        series: [
            {
                name: 'BSOP Class Count',
                type: 'pie',
                radius: ['40%', '70%'],
                label: { show: true, position: 'outside', formatter: '{b}: {c}' },
                data: donutData
            }
        ]
    };

    // Props for summary cards
    interface CountCardProps {
        title: string;
        count: number;
        icon: any;
    }

    // Small reusable component for displaying a count card
    const CountCard: React.FC<CountCardProps> = ({ title, count, icon }) => {
        return (
            <div
                className={
                    theme.isDark == "true"
                        ? "bg-transparent rounded-lg shadow-md p-6 border border-gray-200"
                        : "bg-white rounded-lg shadow-md p-6 border border-gray-200"
                }
            >
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium mb-1">{title}</p>
                        <p className="text-3xl font-bold">{count.toLocaleString()}</p>
                    </div>
                    <div className="text-3xl">{icon}</div>
                </div>
            </div>
        );
    };

    // API calls setup with useFetch
    const fetch = {
        // Fetch dashboard counts (summary numbers)
        getCnts:  useFetch({
            api: () => APIS.getDashCnt(),
            onSuccess: (data) => {
                const counts = data?.cstmrCntDtos?.content;
                if (counts) {
                    setCountsData(counts);
                    setCountData([
                        {
                            title: "Total Users Submit Review",
                            count: counts.submtdUsrCnt,
                            icon: "👥"
                        },
                        {
                            title: "Good Experience Review",
                            count: counts.gdRvewCnt,
                            icon: "👍"
                        },
                        {
                            title: "Bad Experience Review",
                            count: counts.bdRvewCnt,
                            icon: "👎"
                        },
                        {
                            title: "Total Process Registered",
                            count: counts.ttlRegstrdCnt,
                            icon: "🗂️"
                        },
                    ]);

                }
            },
            onError: (err) => modal.openModal({ content: err.response?.data?.errorMessage ?? "Unknown error" }),
        }),

        // Fetch monthly statistics for bar chart
        getMonthStatistic: useFetch({
            api: () => APIS.getMonthStatistic(),
            onSuccess: (data) => {
                const counts = data?.monthlyStats?.content;
                if (counts) {
                    setMonthlyData(counts)
                }
            },
            onError: (err) => modal.openModal({ content: err.response?.data?.errorMessage ?? "Unknown error" }),
        }),

        // Fetch business operation classification counts for donut chart
        fetchBsopCounts: useFetch({
            api: () => APIS.getBsopClsfCnt(),
            onSuccess: (data) => {
                const counts = data?.bsopClsfCounts?.content;
                if (counts) {
                    setBsopCounts(counts);
                }
            },
            onError: (err) => modal.openModal({ content: err.response?.data?.errorMessage ?? "Unknown error" }),
        })
    }

    // Run API calls once on mount
    useEffect(() => {
        fetch.getCnts.fetch();
        fetch.getMonthStatistic.fetch();
        fetch.fetchBsopCounts.fetch();
    }, []);

    return (
        <Page>
            {/* Summary Section with cards */}
            <Group>
                <Group.Header>Summary</Group.Header>
                <Group.Body>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {countData.map((item, index) => (
                            <CountCard key={index} title={item.title} count={item.count} icon={item.icon} />
                        ))}
                    </div>
                </Group.Body>
            </Group>

            {/* Charts Section */}
            <Group>
                <Group.Body>
                    <div className="grid grid-cols-12">
                        {/* Donut Chart */}
                        <div className="col-span-6 bg-blue-200 p-4">
                            <Group>
                                <Group.Header>Charts</Group.Header>
                                <Group.Body>
                                    <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mb-6">
                                        <div className="col-span-6 flex justify-center">
                                            <ReactECharts option={donutOption} theme={chartTheme} style={{ height: 400, width: "100%" }} />
                                        </div>
                                    </div>
                                </Group.Body>
                            </Group>
                        </div>

                        {/* Bar Chart */}
                        <div className="col-span-6 bg-blue-200 p-4">
                            <Group>
                                <Group.Header>Charts</Group.Header>
                                <Group.Body>
                                    <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mb-6">
                                        <div className="col-span-6 flex justify-center">
                                            <ReactECharts option={option} theme={chartTheme} style={{ height: 400, width: "100%" }} />
                                        </div>
                                    </div>
                                </Group.Body>
                            </Group>
                        </div>
                    </div>
                </Group.Body>
            </Group>
        </Page>
    );
};
